// Manifest of available custom artwork for Busy badges.
//
// Files live in apps/mobile/public/assets/badges/ and are served from
// /assets/badges/<filename>. Drop a new file in, add the entry here, and
// the gallery chips, detail modal, celebration modal, and nickname hero
// pick it up automatically. Anything not listed here falls back to the
// gradient + KindIcon (or text-only nickname hero) treatment.
//
// We use an explicit manifest rather than a glob/HEAD-probe so:
//   - Vite knows which assets exist at build time
//   - Adding artwork is a single deliberate edit
//   - The gallery chip render can branch synchronously without a load probe

import type { AchievementKind } from '@busy/api';

/** Achievement-badge artwork keyed as `${kind}-${threshold}`. */
const BADGE_FILES: Record<string, string> = {
  // Lessons — full set of 8 (childhood reading → mythic librarian)
  'lessons-1':    '/assets/badges/lessons-1.jpg',
  'lessons-10':   '/assets/badges/lessons-10.jpg',
  'lessons-25':   '/assets/badges/lessons-25.jpg',
  'lessons-50':   '/assets/badges/lessons-50.jpg',
  'lessons-100':  '/assets/badges/lessons-100.jpg',
  'lessons-250':  '/assets/badges/lessons-250.jpg',
  'lessons-500':  '/assets/badges/lessons-500.jpg',
  'lessons-1000': '/assets/badges/lessons-1000.jpg',
  // Streak / clients / cuts / revenue — pending generation, fall back to
  // the gradient + KindIcon until each entry lands here.
};

/** Returns the public URL for a badge's artwork, or null if no asset exists. */
export function badgeArtworkUrl(kind: AchievementKind, threshold: number): string | null {
  return BADGE_FILES[`${kind}-${threshold}`] ?? null;
}

/** Nickname-tier artwork for the big hero banner on the Achievements page.
 *  Keyed by the same i18n key the nickname helper resolves to (e.g.
 *  `nickname.apprentice`, `nickname.solo.streak`). Anything not listed here
 *  falls back to the text-only gradient hero. */
const NICKNAME_FILES: Record<string, string> = {
  'nickname.newcomer':      '/assets/badges/newcomer.png',
  'nickname.apprentice':    '/assets/badges/apprentice.png',
  'nickname.rising':        '/assets/badges/rising.png',
  'nickname.steady':        '/assets/badges/steady.png',
  'nickname.solo.streak':   '/assets/badges/solo-streak.png',
};

/** Returns the public URL for a nickname-tier banner, or null if none. */
export function nicknameArtworkUrl(nicknameKey: string): string | null {
  return NICKNAME_FILES[nicknameKey] ?? null;
}
